import { useQuery } from '@tanstack/react-query';
import {
  getCategories,
  getCategoriesGroup,
  getCategoriesSubgroup,
} from '../../../api/category';
import { ReactQueryKey } from '../../../constants/ReactQueryKeys';
import { CategoriesGroup, Category } from '../../../types/Category';
import { QueryParamsInterface } from '../../../types/Shared';

export const useCategories = ({
  onSuccess,
  onError,
  params,
}: {
  onSuccess?: (data: any) => void;
  onError?: (err: any) => void;
  params?: QueryParamsInterface;
}) => {
  return useQuery(
    [ReactQueryKey.GET_CATEGORIES, params],
    () => getCategories({ ...params }),
    {
      onSuccess,
      onError,
      enabled: params && !!params,
      keepPreviousData: true,
      staleTime: 30000,
    }
  );
};

export const useCategoryGroup = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: any) => void;
  onError?: (err: any) => void;
}) => {
  return useQuery(['get-categoriesGroup'], getCategoriesGroup, {
    onSuccess,
    onError,
    keepPreviousData: true,
    staleTime: 30000,
  });
};

export const useCategorySubGroup = ({
  onSuccess,
  onError,
  group,
}: {
  onSuccess?: (data: any) => void;
  onError?: (err: any) => void;
  group?: CategoriesGroup;
}) => {
  return useQuery(
    ['get-categoriesSubGroup', group],
    () => getCategoriesSubgroup({ groupId: group?.id }),
    {
      onSuccess,
      onError,
      enabled: group && !!group,
      keepPreviousData: true,
      staleTime: 30000,
    }
  );
};

interface PhaseCategoriesHookProps {
  phase: string;
}

export const usePhaseCategories = ({ phase }: PhaseCategoriesHookProps) => {
  const fetchCategoriesSubGroup = useCategorySubGroup({});
  const categoriesSubGroup = fetchCategoriesSubGroup?.data?.data?.find(
    (cat: CategoriesGroup) => cat.name === phase.toLowerCase()
  );
  const fetchPhaseCategories = useCategories({
    params: { subGroupId: categoriesSubGroup?.id },
  });
  return fetchPhaseCategories;
};

interface CategoryByIdHookProps {
  categoryId: number;
}

export const useCategoryById = ({ categoryId }: CategoryByIdHookProps) => {
  const fetchCategories = useCategories({});
  const category = fetchCategories?.data?.data?.find(
    (cat: Category) => cat.id === categoryId
  );
  return category;
};
